import React from 'react';
import { Link } from 'react-router-dom';

function fmtAddr(a) {
  if (!a) return '—';
  const s = String(a);
  return s.length > 14 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
}

function fmtUsd(n, d = 2) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  if (abs >= 1) return `$${v.toFixed(d)}`;
  if (abs >= 0.0001) return `$${v.toFixed(6)}`;
  return `$${v.toExponential(2)}`;
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}

function pnlClass(n) {
  if (n == null || Number.isNaN(Number(n))) return 'text-gray-500';
  if (Number(n) > 0) return 'text-green-600 dark:text-green-400';
  if (Number(n) < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-300';
}

function reasonLabel(reason) {
  const r = String(reason || 'manual');
  if (r === 'manual') return 'Manual TP';
  if (r.startsWith('auto_')) return `Auto ${r.replace(/^auto_/, '').toUpperCase()}`;
  if (r === 'trailing_stop') return 'Trailing stop';
  if (r === 'auto_2x') return 'Auto TP 2x';
  return r;
}

/**
 * Quick-view popup for a frontrun event (entry MC + live PnL + sells).
 */
export default function FrontrunEventModal({ isOpen, onClose, event, loading }) {
  if (!isOpen) return null;

  const ev = event || {};
  const pnl = ev.pnl || {};
  const live = ev.live || {};
  const sells = Array.isArray(pnl.sells) ? pnl.sells : Array.isArray(ev.sells) ? ev.sells.filter((s) => !s?.error) : [];
  const isLive = ev.dryRun === false || ev.status === 'executed';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div
          className="relative w-full max-w-lg rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {ev.tokenSymbol || 'Token'}
                <span className="ml-2 text-xs font-normal uppercase text-gray-500">{ev.chain}</span>
              </div>
              <div className="text-xs font-mono text-gray-500 mt-0.5">{fmtAddr(ev.tokenAddress)}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="px-5 py-4 space-y-4">
            {loading && !event ? (
              <div className="text-sm text-gray-500 py-8 text-center">Loading live PnL…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                    <div className="text-xs text-gray-500 mb-1">Entry MC</div>
                    <div className="text-base font-semibold text-gray-900 dark:text-white">
                      {fmtUsd(pnl.entryMarketCap ?? ev.marketCap)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      @ {fmtUsd(pnl.entryPriceUsd ?? ev.priceUsd, 6)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                    <div className="text-xs text-gray-500 mb-1">Live MC</div>
                    <div className="text-base font-semibold text-gray-900 dark:text-white">
                      {fmtUsd(pnl.liveMarketCap ?? live.marketCap)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      @ {fmtUsd(pnl.livePriceUsd ?? live.priceUsd, 6)}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="text-xs text-gray-500 mb-2">
                    {isLive ? 'Total PnL (incl. TP sells)' : 'Simulated PnL'}
                  </div>
                  <div className={`text-2xl font-bold ${pnlClass(pnl.pnlPct)}`}>
                    {fmtPct(pnl.pnlPct)}
                  </div>
                  <div className={`text-sm mt-1 ${pnlClass(pnl.pnlUsd)}`}>
                    {fmtUsd(pnl.pnlUsd)}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-gray-500">Cost</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{fmtUsd(pnl.entryUsd)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Remaining MTM</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{fmtUsd(pnl.liveUsd)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Realized (TP)</div>
                      <div className={`font-medium ${pnlClass(pnl.realizedUsd)}`}>{fmtUsd(pnl.realizedUsd)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Sold</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {pnl.percentSold != null ? `${Number(pnl.percentSold).toFixed(0)}%` : '—'}
                      </div>
                    </div>
                  </div>
                  {pnl.mcMultiple != null && (
                    <div className="text-xs text-gray-500 mt-2">
                      MC multiple:{' '}
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {Number(pnl.mcMultiple).toFixed(2)}x
                      </span>
                    </div>
                  )}
                </div>

                {sells.length > 0 && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 dark:bg-gray-800/60">
                      Take-profit / sells
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-40 overflow-y-auto">
                      {sells.map((s, i) => (
                        <div key={s.txHash || i} className="px-3 py-2 text-xs flex justify-between gap-2">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {reasonLabel(s.reason)} · {s.percent}%
                            </div>
                            <div className="text-gray-500">
                              {s.at ? new Date(s.at).toLocaleString() : '—'}
                              {s.txHash ? ` · ${fmtAddr(s.txHash)}` : ''}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {fmtUsd(s.usdReceived)}
                            </div>
                            <div className="text-gray-500">
                              {s.nativeReceived != null
                                ? `${Number(s.nativeReceived).toFixed(4)} native`
                                : '—'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">Wallet</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {ev.monitoredWalletLabel || fmtAddr(ev.monitoredWalletAddress)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Size</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {ev.amountNative} native
                      {ev.dryRun !== false ? ' · dry run' : ' · live'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Tokens left</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {pnl.entryTokens != null
                        ? Number(pnl.entryTokens).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">When</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {ev.createdAt ? new Date(ev.createdAt).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 rounded-b-xl">
            <div className="flex gap-2">
              {ev.links?.dexscreener && (
                <a
                  href={ev.links.dexscreener}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700"
                >
                  Chart
                </a>
              )}
              {ev.links?.explorerTx && (
                <a
                  href={ev.links.explorerTx}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Source tx
                </a>
              )}
            </div>
            {ev._id && (
              <Link
                to={`/frontrun/events/${ev._id}`}
                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={onClose}
              >
                Full page →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { fmtUsd, fmtPct, pnlClass, fmtAddr };
